import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useParams } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Landing from "@/pages/Landing";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import Founder from "@/pages/Founder";
import Privacy from "@/pages/Privacy";
import PartnerPage from "@/pages/PartnerPage";
import SetPassword from "@/pages/SetPassword";
import Terms from "@/pages/Terms";
import GeoLanding, { CONTENT_CLOUDBEDS_ALT, CONTENT_WHATSAPP_HOTEL, CONTENT_RECEPTIONIST_247, CONTENT_VS_OTELLO, CONTENT_VS_HIJIFFY, CONTENT_VS_ASKSUITE } from "@/pages/GeoLanding";
import Compare from "@/pages/Compare";
import { BlogIndex, BlogArticle } from "@/pages/Blog";
import { CaseStudiesIndex, CaseStudyDetail, CaseStudyTestimonialSubmit } from "@/pages/CaseStudy";
import OtelyaWidget from "@/components/OtelyaWidget";
import { useEffect } from "react";
import { trackPageview } from "@/lib/tracker";

function ShortLinkRedirect() {
  const { code } = useParams();
  useEffect(() => { window.location.replace(`${process.env.REACT_APP_BACKEND_URL}/api/r/${code}`); }, [code]);
  return null;
}

function PageTracker() {
  const location = useLocation();
  useEffect(() => { trackPageview(location); }, [location.pathname]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-stone">Caricamento…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AppRouter() {
  const location = useLocation();
  // Detect Google auth callback synchronously (during render) to avoid race conditions
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Show the Otelya HQ chat widget on every public page (not inside the dashboard)
  const isDashboard = location.pathname.startsWith("/dashboard");
  return (
    <>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/blog" element={<BlogIndex />} />
        <Route path="/blog/:slug" element={<BlogArticle />} />
        <Route path="/founder" element={<Founder />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/r/:code" element={<ShortLinkRedirect />} />
        <Route path="/partner" element={<PartnerPage />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/alternative-a-cloudbeds-italia" element={<GeoLanding content={CONTENT_CLOUDBEDS_ALT} path="/alternative-a-cloudbeds-italia" />} />
        <Route path="/whatsapp-automatico-hotel" element={<GeoLanding content={CONTENT_WHATSAPP_HOTEL} path="/whatsapp-automatico-hotel" />} />
        <Route path="/receptionist-24-7-bnb" element={<GeoLanding content={CONTENT_RECEPTIONIST_247} path="/receptionist-24-7-bnb" />} />
        <Route path="/otelya-vs-otello-ai" element={<GeoLanding content={CONTENT_VS_OTELLO} path="/otelya-vs-otello-ai" />} />
        <Route path="/otelya-vs-hijiffy" element={<GeoLanding content={CONTENT_VS_HIJIFFY} path="/otelya-vs-hijiffy" />} />
        <Route path="/otelya-vs-asksuite" element={<GeoLanding content={CONTENT_VS_ASKSUITE} path="/otelya-vs-asksuite" />} />
        <Route path="/compare" element={<Compare />} />
        <Route path="/case-studies" element={<CaseStudiesIndex />} />
        <Route path="/case-study/:slug/testimonial" element={<CaseStudyTestimonialSubmit />} />
        <Route path="/case-study/:slug" element={<CaseStudyDetail />} />
        <Route path="/dashboard/*" element={<Protected><Dashboard /></Protected>} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      {!isDashboard && <OtelyaWidget />}
      {!isDashboard && <PageTracker />}
    </>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <BrowserRouter>
            <AppRouter />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
